import React, { ReactNode, useMemo, useState, useCallback } from 'react';
import { AnimationContext, AnimationHandlersContext } from './AnimationContext';
import { useShouldAnimateOnOpen } from './useShouldAnimateOnOpen';
import { ANIMATION_CLASSES_STATE_CONFIG } from './config';
import { AnimationClassesTypes, PrevAnimationEndItems } from './types';
import { without } from 'ramda';

// Current context can be re-used for different components
// if we will pass CONFIG as prop but for now CONFIG is just a static constant
export const AnimationProvider = ({ children }: { children: ReactNode }) => {
    const [isPrevAnimationEndMap, setPrevAnimationEnd] = useState<PrevAnimationEndItems>({});
    const { shouldAnimateOnOpen, resetAnimateOnOpen } = useShouldAnimateOnOpen();
    const [animationClasses, setAnimationClasses] = useState<AnimationClassesTypes>(ANIMATION_CLASSES_STATE_CONFIG);
    const [animationContainerClasses, setAnimationContainerClasses] = useState<string[]>([]);

    const values = useMemo(
        () => ({
            shouldAnimateOnOpen,
            isPrevAnimationEndMap,
            animationClasses,
            animationContainerClasses
        }),
        [
            shouldAnimateOnOpen,
            isPrevAnimationEndMap,
            animationClasses,
            animationContainerClasses
        ]);

    const handleSetPrevAnimationEnd = useCallback((key: string) => () => {
        setPrevAnimationEnd(prev => ({ ...prev, [key]: true }));
    }, []);

    const handleSetAnimationClasses = useCallback(
        (animationType: string, newItem: { [key: string]: string[] }) => {
            setAnimationClasses(prev => ({
                ...prev,
                [animationType]: {
                    ...prev[animationType as keyof typeof prev],
                    ...newItem
                }
            }));
    }, []);

    const handleSetContainerClasses = useCallback(
        (cn: string) => {
            setAnimationContainerClasses(prevClasses => [...new Set([...prevClasses, cn])]);
        },
        []
    );

    const handleRemoveContainerClasses = useCallback(
        (cn: string) => {
            setAnimationContainerClasses(prevClasses => without([cn], prevClasses));
        },
        []
    );

    const handlers = useMemo(
        () => ({
            resetAnimateOnOpen,
            handleSetPrevAnimationEnd,
            handleSetAnimationClasses,

            handleSetContainerClasses,
            handleRemoveContainerClasses
        }),
    [
        resetAnimateOnOpen,
        handleSetPrevAnimationEnd,
        handleSetAnimationClasses,
        handleSetContainerClasses,
        handleRemoveContainerClasses
    ]);

    return (
        <AnimationContext.Provider value={values}>
            <AnimationHandlersContext.Provider value={handlers}>
                {children}
            </AnimationHandlersContext.Provider>
        </AnimationContext.Provider>
    );
};
